/**
 *  ------------------------------------
 *  Imports
 *  ------------------------------------
 */
import { FunctionComponent, useState, useEffect } from "react";

import { withTranslation, WithTranslation } from 'react-i18next';
import Select from 'react-select'

import {changeLanguage, setHtmlLanguage, setMetaTags} from 'components/i18n/I18n';
import Mobilemenu from 'components/mobilemenu/Mobilemenu';
import configData from 'app-config.json';
import Icon from '../icon/Icon';
import './Header.scss';

/**
 *  ------------------------------------
 *  Component
 *  ------------------------------------
 */
type LanguageOptionsType = {
    value: string,
    label: string
}

const Header: FunctionComponent<WithTranslation & { setLangChange?: (langChange: string) => void }> = ({ t, i18n, setLangChange }) => {

    const [open, setOpen] = useState(false);

    const toggleOpen = () => {
        setOpen(!open);
        document.body.classList.toggle("mobile-menu-open");
    }

    const closeMenu = () => {
        setOpen(false);
        document.body.classList.remove("mobile-menu-open");
    }

    const options:LanguageOptionsType[] = [];
    configData.SUPPORTED_LANGUAGES.forEach((code:string) => {
        const item:LanguageOptionsType = {value: code, label: t("header:language.label."+code, code)};
        options.push(item);
    });

    const [language, setLanguage] = useState<any>(() => {
        let currentLng = '';
        if (window.location.pathname.split('/')[1]) {
            currentLng = window.location.pathname.split('/')[1];
            if (!options.find((item) => item.value === currentLng)) {
                currentLng = i18n.language;
            }
        } else {
            currentLng = i18n.language;
        }
        return options.find((item) => item.value === currentLng);
    });

    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.code === "Escape" && open) {
                closeMenu();
            }
        };
        
        window.addEventListener('keydown', handleEsc);
        window.addEventListener('closeMenu', closeMenu);
        return () => {
            window.removeEventListener('keydown', handleEsc);
            window.removeEventListener('closeMenu', closeMenu);
        };
    })

    useEffect(() => {
        (async () => {
            await changeLanguageHandler(language);

            if (setLangChange) {
                setLangChange(language.value);
            }
        })();
    }, [language, setLangChange])

    const changeLanguageHandler = async (option:LanguageOptionsType) => {
        await changeLanguage(option.value);
        setHtmlLanguage(option.value);
        setMetaTags(option.value);
        window.dispatchEvent(new CustomEvent("languageChange", {detail: option.value}));
    };

    // override react select option to add no translate attribute
    const formatOptionLabel = ({ label } : any) => <div translate="no">{label}</div>;

    return (
        <header>
            <div className="inner">
                <a href="http://www.inselgruppe.ch/" className="logo" title={t('header:logo.alt')} aria-label={t('header:logo.alt')}>
                    <svg className="full-logo" width="600" height="85" viewBox="0 0 600 85" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g className="word">
                            <path d="M342.655 55.0938L342.354 76.6424C339.25 78.0521 335.145 78.8576 331.339 78.8576C318.422 78.8576 309.41 68.5868 309.41 55.7986C309.41 43.0104 318.522 32.7396 331.339 32.7396C336.847 32.7396 342.254 34.3507 345.859 36.3646L348.462 31.0278C343.255 28.4097 337.247 27 331.339 27C314.917 27 302 38.9827 302 56C302 74.2257 314.917 85 331.339 85C336.346 85 343.055 83.993 349.263 80.8715L349.564 55.3958H342.655M372.995 84.3958V59.9271H377.301C382.608 59.9271 384.21 63.25 387.615 67.5799L399.831 84.3958H408.443L395.526 66.875C393.323 63.9549 390.218 59.0208 386.413 58.2153V58.0139C394.224 57.007 400.432 51.5694 400.432 42.6076C400.432 33.4444 393.323 28.0069 382.808 28.0069H366.086V84.1945H372.995V84.3958ZM373.196 32.8403H381.507C390.519 32.8403 393.723 38.0764 393.723 43.9167C393.723 52.0729 386.614 54.4896 379.905 54.4896H372.995L373.196 32.8403ZM453.704 28.0069L453.503 62.1424C453.503 74.8299 446.594 78.757 438.483 78.757C428.87 78.757 423.463 74.0243 423.463 64.0556C423.463 58.8195 423.463 53.684 423.663 48.4479L423.864 28.0069H416.954L416.754 47.2396C416.554 52.7778 416.554 58.0139 416.554 62.5452C416.554 79.3611 426.067 84.7986 438.083 84.7986C448.997 84.7986 460.212 80.4687 460.513 62.7465L460.713 28.0069H453.704ZM484.845 32.8403H493.157C502.169 32.8403 505.373 38.0764 505.373 43.9167C505.373 51.8715 499.165 55.1944 493.457 55.1944H484.645L484.845 32.8403ZM484.645 84.3958V60.8333H492.556C501.668 60.8333 512.182 56.5035 512.182 44.2188C512.182 33.6458 505.073 28.2083 494.558 28.2083H477.836V84.3958H484.645V84.3958ZM531.007 32.8403H539.319C548.331 32.8403 551.535 38.0764 551.535 43.9167C551.535 51.8715 545.327 55.1944 539.619 55.1944H530.907L531.007 32.8403ZM530.907 84.3958V60.8333H538.818C547.93 60.8333 558.444 56.5035 558.444 44.2188C558.444 33.6458 551.335 28.2083 540.821 28.2083H524.098L523.898 84.3958H530.907V84.3958ZM599.9 84.1945V78.2535H577.47L577.67 58.6181H598.899V52.6771H577.67L577.87 33.5451H599.8V27.6042H571.161V83.7917H600V84.1945H599.9Z" fill="#677078" />
                            <path d="M195.631 31.759C193.434 30.3709 190.838 29.1812 188.241 28.4872C185.745 27.7932 183.248 27.4957 180.951 27C175.459 27 170.865 28.8837 167.57 31.759C164.374 34.8325 162.277 39.0957 162.277 43.8547C162.277 53.9222 169.7 57.2098 176.17 60.0758L176.258 60.1145C181.051 62.3949 185.545 64.1795 185.545 68.8393C185.545 71.1196 184.547 72.8051 183.148 73.9949C181.551 75.3829 179.154 76.0769 176.757 76.0769C173.262 76.0769 169.167 73.6974 165.872 72.012C165.373 71.8137 164.973 71.5162 164.474 71.3179L164.174 71.1197L163.975 71.3179L159.281 79.0513L159.081 79.3487L159.381 79.547C161.778 80.935 164.574 82.3231 167.67 83.3145C170.566 84.306 173.861 85 177.256 85C182.749 85 187.343 83.2154 190.638 80.3402C193.934 77.4649 195.831 73.4 195.831 68.9384V68.7402C195.831 58.0325 188.241 54.959 181.85 52.1829C177.157 50.2 172.862 48.3162 172.862 43.8547C172.862 41.7726 173.661 39.7897 175.259 38.4017C176.657 37.212 178.854 36.3197 181.451 36.3197C182.849 36.3197 184.347 36.8154 185.945 37.3111L185.945 37.3112C187.842 38.2035 189.739 39.0957 191.138 40.0872L191.437 40.2855L191.637 39.988L195.631 32.2547L195.831 31.9573L195.631 31.759Z" fill="#677078" />
                            <path d="M259.444 28.388L259.245 84.2068V84.306H292V74.8872H270.33L270.13 28.388H259.444Z" fill="#677078" />
                            <path d="M211.11 28.388L210.911 84.2068V84.306H241.369V74.8872H221.596L221.796 60.4119H241.069V51.4889H221.596V37.8068H241.369V28.388H211.11Z" fill="#677078" />
                            <path d="M100.361 27.9915L100.561 84.2068L110.747 84.306L110.547 43.9539L135.014 84.6034H147.098V28.388H136.911L136.712 69.0376L111.746 27.9915H100.361Z" fill="#677078" />
                            <path d="M72 28.388V84.2068H82.8852V28.388H72Z" fill="#677078" />
                        </g>
                        <g className="sign">
                            <path d="M22.8 67H27.1H30.6V19.4C30.6 10.1 41.5 3.89998 45.6 2.69998L49.4 8.60001L49.3749 8.6088C47.3355 9.32244 39.6 12.0294 39.6 19.1V60.6C47.7 54.9 53.4 48 53.4 38.4V0H0V38.4C0 48 5.7 54.9 13.8 60.6V19.1C13.8 12 6.1 9.50001 4 8.60001L7.8 2.69998C12.1 4.09998 22.8 10.3 22.8 19.4V67Z" fill="#677078" />
                        </g>
                    </svg>
                    <svg className="mobile-logo" width="54" height="67" viewBox="0 0 54 67" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.8 67H27.1H30.6V19.4C30.6 10.1 41.5 3.89998 45.6 2.69998L49.4 8.60001L49.3749 8.6088C47.3355 9.32244 39.6 12.0294 39.6 19.1V60.6C47.7 54.9 53.4 48 53.4 38.4V0H0V38.4C0 48 5.7 54.9 13.8 60.6V19.1C13.8 12 6.1 9.50001 4 8.60001L7.8 2.69998C12.1 4.09998 22.8 10.3 22.8 19.4V67Z" fill="#677078" />
                    </svg>
                </a>

                <nav className="">
                    <div className="adm-dropdown">
                            <Select
                                className="language-select"
                                classNamePrefix="language-select"
                                onChange={setLanguage}
                                isSearchable={false}
                                options={options}
                                defaultValue={language}
                                aria-label={t('header:button.change.language')}
                                formatOptionLabel={formatOptionLabel}
                            />
                    </div>

                    <button className="mobile-toggle" aria-expanded={open} aria-label="Menu" aria-controls="mobile-menu" id="mobile-menu-trigger" onClick={toggleOpen}>
                        <>{(open) ? <Icon name={"/icons.svg#close"}></Icon> : <Icon name={"/icons.svg#menu"}></Icon>} </> </button>
                </nav>

            </div>
            <Mobilemenu></Mobilemenu>
        </header>
    )
};

export default withTranslation(['header'])(Header);
